import { useLocation, useNavigate, useRouter, useRouterState, type LinkProps } from '@tanstack/react-router';
import { IconButton, Skeleton, Tab, Tabs, Tag, Typography } from '@volvo/vcdk-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { ITelemetryService } from '@/service/vpc/types/ITelemetryService';
import type { ITelemetryDeviceInfo } from '@/service/vpc/types/IUnit';
import type { RouterItem } from '@/types/RouterItem';

import { withComponentErrorBoundary } from '@/components/ComponentErrorBoundary';
import { enviroment, telemetry } from '@/utils';

type Props = {
  isLoading: boolean;
  title: string;
  subTitle?: string;
  items: RouterItem[];
  isClaimingUnit?: boolean;
  backButton?: React.ReactNode;
  avatar?: React.ReactNode;
  actions?: React.ReactNode;
  telemetryDeviceInfo?: ITelemetryDeviceInfo | ITelemetryDeviceInfo[] | null;
  connectivityStatus?: string | null;
  serviceStatuses?: ITelemetryService[] | null;
};

const PageHeader: React.FC<Props> = ({
  isLoading = false,
  title,
  subTitle,
  isClaimingUnit,
  backButton,
  items,
  actions,
  avatar,
  telemetryDeviceInfo,
  connectivityStatus,
  serviceStatuses,
}) => {
  const router = useRouter();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // While a navigation is pending (loader running), use the last resolved location
  // so the header stays in sync with the Outlet content — avoids a flash where tabs
  // disappear / title changes before the new page content is ready.
  const isRouterPending = useRouterState({ select: (s) => s.status === 'pending' });
  const resolvedLocation = useRouterState({ select: (s) => s.resolvedLocation });
  const activePathname = isRouterPending && resolvedLocation ? resolvedLocation.pathname : location.pathname;

  const page = useRouterState({
    select: (s) => s.location.state.page,
  });

  const handleTabClick = (to: LinkProps['to'], params?: LinkProps['params']) => {
    navigate({ to, params, state: { page } });
  };

  const handleTabHover = (to: LinkProps['to'], params?: LinkProps['params']) => {
    router.preloadRoute({ to, params });
  };

  // Helper to build full path with params
  const buildPathWithParams = (to: LinkProps['to'], params?: LinkProps['params']): string => {
    let fullPath = to as string;
    if (params && typeof params === 'object' && !Array.isArray(params)) {
      Object.entries(params).forEach(([key, value]) => {
        fullPath = fullPath.replace(`$${key}`, String(value));
      });
    }
    return fullPath;
  };

  // Helper to check if a template route matches the current location
  const matchesTemplateRoute = (template: string, pathname: string): boolean => {
    // Split both template and pathname into segments
    const templateSegments = template.split('/').filter(Boolean);
    const pathnameSegments = pathname.split('/').filter(Boolean);

    // If lengths don't match, it's not a match
    if (templateSegments.length !== pathnameSegments.length) return false;

    // Compare each segment
    return templateSegments.every((segment, index) => {
      // If it's a parameter (starts with $), it matches any value
      if (segment.startsWith('$')) return true;
      // Otherwise, it must match exactly
      return segment === pathnameSegments[index];
    });
  };

  // Helper to check if a path matches the current location
  const isPathActive = (
    to: LinkProps['to'],
    params?: LinkProps['params'],
    pathname: string = activePathname
  ): boolean => {
    const template = to as string;

    // If params are provided, build the full path and do exact matching
    if (params && typeof params === 'object' && !Array.isArray(params)) {
      const fullPath = buildPathWithParams(to, params);
      // Compare both the raw path and decoded version to handle URL encoding
      const decodedPathname = decodeURIComponent(pathname);
      const decodedFullPath = decodeURIComponent(fullPath);

      return (
        pathname === fullPath ||
        pathname.startsWith(`${fullPath}/`) ||
        decodedPathname === decodedFullPath ||
        decodedPathname.startsWith(`${decodedFullPath}/`)
      );
    }

    // If no params, check if it's a template route or direct URL
    if (template.includes('$')) {
      // It's a template route, match the structure
      return matchesTemplateRoute(template, pathname);
    }

    // It's a direct URL, do exact matching
    return pathname === template || pathname.startsWith(`${template}/`);
  };

  const isTabActive = (to: LinkProps['to'], params?: LinkProps['params']) => {
    return isPathActive(to, params);
  };

  // Find the current child route and its parent in a single pass
  const findChildRouteContext = () => {
    for (const item of items) {
      if (!item.children?.length) continue;

      const matchingChild = item.children.find((child) => {
        return isPathActive(child.to, child.params);
      });

      if (matchingChild) {
        return {
          child: matchingChild,
          parent: { to: item.to, params: item.params },
        };
      }
    }
    return null;
  };

  const childRouteContext = findChildRouteContext();
  const displayTitle = childRouteContext?.child.title ?? title;
  const displaySubTitle = childRouteContext?.child.subTitle ?? subTitle;

  const renderStatusTag = (): React.ReactNode => {
    if (enviroment.isProd) return null;

    const hasDevice = telemetry.getTelemetryDevices(telemetryDeviceInfo).length > 0;
    const hasActive = telemetry.hasActiveService(serviceStatuses);
    if (!hasDevice || !hasActive) return null;

    const variant = telemetry.getConnectivityStatusVariant(connectivityStatus);
    if (!variant) return null;

    return (
      <Tag variant={variant === 'success' ? 'success-subtle' : 'neutral-subtle'} size="medium">
        {variant === 'success' ? 'Online' : 'Offline'}
      </Tag>
    );
  };

  return (
    <div className="mb-6 flex flex-col gap-2">
      <div className="flex flex-col gap-5 md:flex-row">
        <div className="flex h-11 flex-1 items-center gap-5">
          {childRouteContext ? (
            <IconButton
              icon="arrow-left"
              size="large"
              onClick={() =>
                navigate({
                  to: childRouteContext.parent.to,
                  params: childRouteContext.parent.params,
                  state: { page },
                })
              }
            />
          ) : (
            backButton
          )}
          {avatar}
          <div className="flex flex-1 flex-col">
            <div className="flex items-center gap-5">
              {isLoading ? (
                <Skeleton variant="typography" typographyVariant="heading3" width={140} />
              ) : (
                <Typography variant="heading3">{displayTitle}</Typography>
              )}
              {renderStatusTag()}
              {isClaimingUnit && (
                <Tag startIcon="info" variant="neutral-subtle" size="small">
                  {t(($) => $.components.table.unit_table.unverified_ownership)}
                </Tag>
              )}
            </div>
            {isLoading ? (
              <Skeleton variant="typography" typographyVariant="body" width={100} />
            ) : (
              !!displaySubTitle && <Typography variant="body">{displaySubTitle}</Typography>
            )}
          </div>
        </div>
        <div className="ml-auto flex w-full items-center gap-3 md:w-fit md:justify-end">
          {childRouteContext ? null : actions}
        </div>
      </div>

      {!!items.length && !childRouteContext && (
        <div className="border-b-subtle border-b">
          <Tabs>
            {items
              .filter((item) => !item.hide)
              .map((item) => (
                <Tab
                  key={item.to}
                  active={isTabActive(item.to, item.params)}
                  onClick={() => handleTabClick(item.to, item.params)}
                  onMouseEnter={() => handleTabHover(item.to, item.params)}
                >
                  {item.title}
                </Tab>
              ))}
          </Tabs>
        </div>
      )}
    </div>
  );
};

export default withComponentErrorBoundary(PageHeader);
